import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  getMyProUsers,
  formatDate,
  formatRemaining,
  formatMoney,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// MY PRO USERS  (Step 15)
//
// "Show only that creator's subscribers" — and it can only ever show them,
// because the server's query is scoped to the signed-in creator's own id.
// There is no creator parameter on the endpoint to point somewhere else.
//
// Columns: User, Payment, Expiry, Remaining Time, Status — the five the
// specification lists, all computed on the server (the same function that
// feeds the reader's own "My Pro Access" screen).
//
// This is a subscriber list, not an analytics screen: who paid, how much, and
// how long they have left. No charts, no trends, no per-reader behaviour.
// =====================================================

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expired", label: "Expired" },
];

function ProUsers() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getMyProUsers());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your Pro users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.items;
    return data.items.filter((i) => i.status === filter);
  }, [data, filter]);

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">👥 MY PRO USERS</div>
          <h1 className="pro-hero-title">Your paying readers</h1>
          {data && (
            <p className="pro-hero-sub">
              {data.activeCount} active · {data.expiredCount} expired ·{" "}
              {data.totalCount} total
            </p>
          )}
        </header>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="pro-tabs" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  className={`pro-tab${filter === f.key ? " pro-tab-active" : ""}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className="pro-card pro-empty">
                <p>
                  {data.totalCount === 0
                    ? "Nobody has unlocked your Pro blogs yet. Share your Pro page to get your first paying reader."
                    : "No readers match this filter."}
                </p>
              </div>
            ) : (
              /* The table scrolls sideways inside its own container on a narrow
                 screen — the page itself never scrolls horizontally. */
              <div className="pro-table-scroll">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Payment</th>
                      <th>Expiry</th>
                      <th>Remaining</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row._id}>
                        <td>
                          <div className="pro-table-user">
                            {row.user.profilePic ? (
                              <img src={row.user.profilePic} alt={row.user.name} />
                            ) : (
                              <span className="pro-blog-avatar-fallback">
                                {(row.user.name || "?").charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span>{row.user.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="pro-table-strong">
                            {formatMoney(row.amountRupees)}
                          </span>
                          <small className="pro-table-sub">
                            {formatDate(row.paymentDate)}
                          </small>
                        </td>
                        <td>{formatDate(row.expiryDate)}</td>
                        <td>
                          {row.status === "active" ? formatRemaining(row.remainingMs) : "—"}
                        </td>
                        <td>
                          <span className={`pro-status-pill pro-status-${row.status}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/pro-home")}
        >
          ← Pro Home
        </button>
      </div>
    </div>
  );
}

export default ProUsers;
